<template>
  <div class="product-buy-forms">
    <!-- お得なセット割り（`QBM01` のみ。支給は定期と同じ `_regular` 枠で描く・決定#48-③） -->
    <PartsProductBuyBox
      v-if="setBox"
      variant="regular"
      :head="setBox.head"
      :title="setBox.name"
      :tags="setBox.tags"
      :price="setPrice"
      :discount="setDiscountRate"
      discount-unit="%"
      :button-label="setBox.buttonLabel"
      @submit="submitSet"
    >
      <template #fields>
        <PartsProductBuyFields :note="setFieldsNote">
          <PartsProductBuyField
            id="set_qty"
            label="数量"
            :options="setQuantityOptions"
            :model-value="setQuantity"
            @update:model-value="setQuantity = Number($event)"
          />
        </PartsProductBuyFields>
      </template>
    </PartsProductBuyBox>

    <!-- 定期コース -->
    <PartsProductBuyBox
      v-if="regularBox"
      variant="regular"
      :head="regularBox.head"
      :title="regularBox.name ?? product.regularName ?? product.name"
      :tags="regularBox.tags"
      :price="regularPrice"
      :discount="regularDiscount"
      :button-label="regularBox.buttonLabel"
      @submit="submitRegular"
    >
      <PartsProductBuyInfo
        v-if="regularBox.info"
        :body="regularInfoBody"
        :link="regularBox.info.link ?? null"
      />
      <!-- `upcell` の枠は数量も間隔も固定なのでセレクタを出さない（`NST`・決定#69） -->
      <template v-if="!regularBox.upcell" #fields>
        <PartsProductBuyFields :note="regularBox.fieldsNote">
          <PartsProductBuyField
            id="regular_qty"
            label="数量"
            note="/1回あたり"
            :options="regularQuantityOptions"
            :model-value="regularQuantity"
            @update:model-value="regularQuantity = Number($event)"
          />
          <PartsProductBuyField
            id="regular_interval"
            label="お届けの間隔"
            :options="cycleOptions"
            :model-value="cycle"
            @update:model-value="cycle = Number($event)"
          />
        </PartsProductBuyFields>
      </template>
    </PartsProductBuyBox>

    <!-- 通常購入 -->
    <PartsProductBuyBox
      v-if="normalBox"
      variant="normal"
      :head="normalBox.head"
      :title="product.name"
      :tags="normalBox.tags"
      :price="normalPrice"
      :unit-label="normalBox.unitLabel"
      :button-label="normalButtonLabel"
      :has-regular="hasRegular"
      @submit="submitNormal"
    >
      <PartsProductBuyInfo
        v-if="normalBox.info"
        :body="normalInfoBody"
        :link="normalBox.info.link ?? null"
      />
      <template #fields>
        <PartsProductBuyFields :note="normalBox.fieldsNote">
          <!--
            KENCOS 5商品だけセレクタが3本立て。並びは **色 → フレーバー → 数量**（`V-22`）。
            支給は 色 → 数量 → フレーバー だが、現行本番の並びに戻す指摘があった。
            数量は KENCOS 以外でも出るので、KENCOS のときだけ順序が変わる形になる。
          -->
          <PartsProductBuyField
            v-if="isKencos"
            id="normal_color"
            label="色を選択"
            :options="PRODUCT_CONSTANTS.KENCOS.COLORS"
            :model-value="selectedColor"
            @update:model-value="selectedColor = $event"
          />
          <PartsProductBuyField
            v-if="isKencos"
            id="normal_flavor"
            label="フレーバー"
            :options="PRODUCT_CONSTANTS.KENCOS.FLAVORS"
            :model-value="selectedFlavor"
            @update:model-value="selectedFlavor = $event"
          />
          <PartsProductBuyField
            id="normal_qty"
            label="数量"
            :options="normalQuantityOptions"
            :model-value="normalQuantity"
            @update:model-value="normalQuantity = Number($event)"
          />
        </PartsProductBuyFields>
      </template>
    </PartsProductBuyBox>
  </div>
</template>

<script setup lang="ts">
/**
 * 商品詳細の購入枠（支給デザインの `product-buy-forms`）。
 * **カートへの結線をここに集約する**。旧実装（撤去済み）の購入フォーム3本を1本にまとめたもので、
 * ビジネスルールはすべて旧実装から移植した（決定#46・#48）。
 *
 * 枠は最大2つ出る。**セット割と定期は排他**（支給38本を全数確認。`QBM01` だけがセット割で、
 * この商品は `regularPrice` が null ＝定期を持たない）。
 *
 * | 枠 | 出る条件 | カート投入コード | 数量の範囲 |
 * |---|---|---|---|
 * | セット割 | `content.setBox` | `setBox.code`（`QBS01`。表示中の商品とは別物） | `setBox.min`〜`max` |
 * | 定期 | `content.regularBox` ＋ API の `regularCode` | threshold があればその `code`、無ければ `regularCode` | threshold か `regularMinQuantity`〜`regularMaxQuantity` |
 * | 通常 | `content.normalBox` | KENCOS は色×フレーバーの組み合わせコード、他は `product.code` | `lowerLimit`〜`upperLimit` |
 *
 * **`NST` は通常枠も定期枠も出ない**（API の `price` が 0・`regularCode` が null。決定#48-②）。
 * const 側も `normalBox` を持たないので、この部品は何も描かない。
 *
 * **金額はハードコードしない**（§3.7）。一括購入割引の行も `discounts` ＋ `price` から導出する。
 */
import type { CartType, ProductType, ProductDetailContentType } from '@/types/domains/index';
import { ProductPresenter } from '@/presenters/ProductPresenter';
import { range } from '@/utils/ArrayHelper';
import { PRODUCT_CONSTANTS } from '@/consts/ProductConst';
import { KENCOS_NOTICE_CONSTANTS } from '@/consts/KencosNoticeConst';

type Props = {
  product: ProductType.Product;
  content: ProductDetailContentType.ProductDetailContent;
};

const props = defineProps<Props>();

const productPresenter = new ProductPresenter();
const config = useRuntimeConfig();
const { show } = useDialog();

const toOptions = (values: number[], suffix = ''): { value: number; label: string }[] =>
  values.map((value) => ({ value, label: `${value}${suffix}` }));

// ---------------------------------------------------------------- 一括購入割引

/**
 * `■3箱以上で20%OFF！【1箱あたり8,000円（税込）】` を `discounts` から作る。
 * 支給はこの行をベタ書きしているが、**導出にすると支給の誤り2件が併せて直る**
 * （`710` の他商品からのコピペ／`777` の誤った割引率）。割引率は切り捨て（§3.3.8）。
 */
const discountLines = computed<ProductDetailContentType.ProductBuyInfoLine[]>(() => {
  const unit: string = props.content.normalBox?.unitLabel ?? '個';

  return [...props.product.discounts]
    .sort((a, b) => a.product_quantity - b.product_quantity)
    .map((discount) => {
      const rate: number = Math.floor(
        ((props.product.price - discount.discount_quantity) / props.product.price) * 100
      );

      return {
        text: `■${discount.product_quantity}${unit}以上で${rate}%OFF！【1${unit}あたり${discount.discount_quantity.toLocaleString()}円（税込）】`,
        strong: true,
      };
    });
});

/** `lead` → 割引行 → `extra` → `trail` の順に組み立てる（支給の並び） */
const buildInfoBody = (
  info: ProductDetailContentType.ProductBuyInfo
): ProductDetailContentType.ProductBuyInfoLine[] => [
  ...(info.lead ?? []),
  ...(info.discounts ? discountLines.value : []),
  ...(info.extra ?? []),
  ...(info.trail ?? []),
];

// ---------------------------------------------------------------- 定期コース

/**
 * 定期枠を出すか。**通常は API の `regularCode` があること**が条件だが、`NST` だけは
 * 商品実体を持たず `regularCode` が null のまま定期枠を出す（決定#69）。
 * 実態は `RNS1` `RNS2` `RNS3` の定期3商品で、カート投入は `upcellUrl` への遷移で行う。
 */
const regularBox = computed<ProductDetailContentType.ProductBuyBox | undefined>(() =>
  props.product.regularCode || props.content.regularBox?.upcell
    ? props.content.regularBox
    : undefined
);

const hasThresholds: boolean = props.product.quantityThresholdCodes.length > 0;
const currentThreshold = ref<ProductType.QuantityThresholdCode | null>(null);

const regularQuantityOptions = computed<{ value: number; label: string }[]>(() => {
  if (hasThresholds) {
    // threshold 全体の最小値・最大値をとる（`regularForm.vue:137-148`）
    const min: number = Math.min(...props.product.quantityThresholdCodes.map((t) => t.lowerLimit));
    const max: number = Math.max(...props.product.quantityThresholdCodes.map((t) => t.upperLimit));

    return toOptions(range(min, max));
  }

  return toOptions(
    range(props.product.regularMinQuantity ?? 1, props.product.regularMaxQuantity ?? 1)
  );
});

/**
 * 配送間隔は数量に連動する。threshold を持つ7商品は「選択中の数量」で該当帯を引き、
 * その `availableCycles` を出す（決定#47 の訂正・`regularForm.vue:149-161`）。
 */
const cycleOptions = computed<{ value: number; label: string }[]>(() => {
  if (hasThresholds) {
    const threshold = props.product.quantityThresholdCodes.find(
      (t) => regularQuantity.value >= t.lowerLimit && regularQuantity.value <= t.upperLimit
    );

    return toOptions(threshold?.availableCycles ?? [], 'ヵ月毎');
  }

  return toOptions(props.product.regularCycle ?? [], 'ヵ月毎');
});

const regularQuantity = ref<number>(regularQuantityOptions.value[0]?.value ?? 1);
// 数量に連動して選び直すので、初期値は下の `watch`（`immediate`）が入れる
const cycle = ref<number>(1);

const regularUnitPrice = computed<number>(() =>
  hasThresholds && currentThreshold.value
    ? currentThreshold.value.price
    : (props.product.regularPrice ?? 0)
);
// `upcell` の枠は API が価格を返さないので const の表示値を使う（`NST`・決定#69）
const regularPrice = computed<number>(() =>
  regularBox.value?.upcell
    ? (regularBox.value.price ?? 0)
    : regularUnitPrice.value * regularQuantity.value
);
/** 表示はしない（決定#88）。`upcell` 枠の OFF 額の算出にだけ使う */
const regularOldPrice = computed<number | null>(() =>
  regularBox.value?.upcell
    ? (regularBox.value.oldPrice ?? null)
    : props.product.price * regularQuantity.value
);

/**
 * 割引額。**既存 `regularForm.vue:172-198` の式をそのまま移植する**（本番のルールが正・決定#48）。
 *
 * threshold を持つ7商品では **1つ下の数量帯との差額**になる。`501` を数量2で選ぶと
 * 通常価格ではなく数量1の定期単価（8,500円）を基準にして (8,500 − 8,000) × 2 = 1,000円OFF。
 * 「1段まとめると更にいくら安くなるか」を出している。
 * 支給の既定表示（数量1）は 1,500円OFF で、この式でも一致する。
 *
 * `777`（ファニーナイト）だけは定期価格ではなく `discounts` を基準にする特例。
 */
const regularDiscount = computed<number>(() => {
  // `upcell` の枠は定価との差そのもの（`NST` は 20,000 − 15,000 ＝ 5,000円）
  if (regularBox.value?.upcell) return (regularOldPrice.value ?? 0) - regularPrice.value;

  const { code, discounts, price, regularPrice: unitPrice, quantityThresholdCodes } = props.product;

  if (code === '777' && discounts.length > 0) {
    const discount: ProductType.Discount = discounts[0];

    return (
      (discount.discount_quantity * discount.product_quantity - (unitPrice as number)) *
      regularQuantity.value
    );
  }

  if (hasThresholds && currentThreshold.value) {
    // 数量帯のうち最も下の帯を基準にする。選択中がその帯自身なら通常価格が基準
    const lowest: ProductType.QuantityThresholdCode = quantityThresholdCodes.reduce(
      (accumulator, current) =>
        current.lowerLimit < accumulator.lowerLimit ? current : accumulator,
      quantityThresholdCodes[0]
    );
    const standardPrice: number =
      lowest.price === currentThreshold.value.price ? price : lowest.price;

    return (standardPrice - currentThreshold.value.price) * regularQuantity.value;
  }

  // 定期1単位がセットの商品（`L61` `L62` の3袋セット）。`regularPrice` はセットぶんの
  // 金額なので、1個あたりの `price` と引き算すると次元が合わず負になる（バッジが消える）
  const setQuantity: number | undefined = regularBox.value?.setQuantity;
  if (setQuantity) {
    return (price * setQuantity - (unitPrice as number)) * regularQuantity.value;
  }

  return (price - (unitPrice as number)) * regularQuantity.value;
});

const regularInfoBody = computed<ProductDetailContentType.ProductBuyInfoLine[]>(() => {
  const info = regularBox.value?.info;
  if (!info) return [];
  const minCount: number = props.content.regularCourse?.minCount ?? 3;

  // 継続回数は `regularCourse.minCount` を単一の出どころにして、購入枠と
  // 「定期コースについて」で回数がずれるのを防ぐ（決定#47）
  return buildInfoBody(info).map((line) => ({
    ...line,
    text: line.text.replace(/\d+回以上/, `${minCount}回以上`),
  }));
});

watch(
  regularQuantity,
  () => {
    if (hasThresholds) {
      currentThreshold.value =
        props.product.quantityThresholdCodes.find(
          (t) => regularQuantity.value >= t.lowerLimit && regularQuantity.value <= t.upperLimit
        ) ?? null;
    }
    // 数量を変えると選べる間隔が変わるので、選択中の間隔が消えたら先頭へ戻す
    const cycles: number[] = cycleOptions.value.map((option) => option.value);
    if (!cycles.includes(cycle.value)) cycle.value = cycles[0];
  },
  { immediate: true }
);

const submitRegular = async (): Promise<void> => {
  /**
   * `upcell` の枠はカート API を叩かず、`upcellUrl` へ遷移して**定期3商品をまとめて**入れる
   * （`NST` → `/cart/set?code[]=RNS1&code[]=RNS2&code[]=RNS3&cycle=1&quantity=1`。決定#69）。
   * 数量と間隔は URL 側で固定されているので、セレクタの値は使わない。
   */
  if (regularBox.value?.upcell) {
    if (props.product.upcellUrl) await navigateTo(props.product.upcellUrl);

    return;
  }

  const params: CartType.PostCartParams = {
    code:
      hasThresholds && currentThreshold.value
        ? currentThreshold.value.code
        : (props.product.regularCode as string),
    quantity: regularQuantity.value,
    cycle: cycle.value,
  };

  await productPresenter.postCart(params);
  await navigateTo('/cart');
};

// ---------------------------------------------------------------- セット割

const setBox = computed(() => props.content.setBox ?? undefined);

const setQuantity = ref<number>(props.content.setBox?.min ?? 1);
const setQuantityOptions = computed<{ value: number; label: string }[]>(() =>
  toOptions(range(props.content.setBox?.min ?? 1, props.content.setBox?.max ?? 1))
);

const setPrice = computed<number>(() => (props.content.setBox?.price ?? 0) * setQuantity.value);
/** セットにした本数ぶんの定価。表示はしない（決定#88）。`10%OFF!` の率の算出にだけ使う */
const setOldPrice = computed<number>(
  () => props.product.price * (props.content.setBox?.perSet ?? 1) * setQuantity.value
);
const setDiscountRate = computed<number>(() =>
  setOldPrice.value === 0
    ? 0
    : Math.floor(((setOldPrice.value - setPrice.value) / setOldPrice.value) * 100)
);

/**
 * 「セット割りで3本ご注文いただくと、1本あたり1,350円となります。」
 * 支給はこれをベタ書きしたうえ、定期枠から「お届けの間隔をご選択ください」の2行を
 * コピペしていた（セット割に間隔の選択は無い）。**金額と本数は導出して1行だけ出す**（§3.7）。
 */
const setFieldsNote = computed<string[]>(() => {
  const box = props.content.setBox;
  if (!box) return [];
  const unit: string = box.unitLabel ?? '本';

  return [
    `セット割りで${box.perSet}${unit}ご注文いただくと、1${unit}あたり${Math.floor(
      box.price / box.perSet
    ).toLocaleString()}円となります。`,
  ];
});

const submitSet = async (): Promise<void> => {
  const params: CartType.PostCartParams = {
    code: props.content.setBox?.code as string,
    // 選択したセット数（1セット ＝ `perSet` 本）
    quantity: setQuantity.value,
  };

  await productPresenter.postCart(params);
  await navigateTo('/cart');
};

// ---------------------------------------------------------------- 通常購入

const normalBox = computed(() => props.content.normalBox);
const hasRegular = computed<boolean>(() => Boolean(regularBox.value || setBox.value));

const isKencos: boolean = PRODUCT_CONSTANTS.KENCOS.SERIES_CODE.includes(props.product.code);

const selectedColor = ref<string>(isKencos ? props.product.code : 'K41');
const selectedFlavor = ref<string>(PRODUCT_CONSTANTS.KENCOS.FLAVORS[0].value);
const normalQuantity = ref<number>(props.product.lowerLimit);
const normalQuantityOptions = computed<{ value: number; label: string }[]>(() =>
  toOptions(range(props.product.lowerLimit, props.product.upperLimit))
);

/**
 * 通常枠は「1箱あたり◯円」＝**単価**を出す（支給38本すべて数量を掛けていない）。
 * KENCOS は本体とフレーバーの組み合わせ商品なので、API の `price` ではなく定数の本体価格を使う
 * （`form.vue:111-115`）。
 */
const normalPrice = computed<number>(() =>
  isKencos ? PRODUCT_CONSTANTS.KENCOS.ORIGINAL_PRICE : props.product.price
);

const normalInfoBody = computed<ProductDetailContentType.ProductBuyInfoLine[]>(() =>
  normalBox.value?.info ? buildInfoBody(normalBox.value.info) : []
);

/**
 * 販売制限の2商品（`KG02` `KF15`）は押してもカートに入らないので「カートに入れる」と書かない。
 * 支給デザインは他36商品と同じ「カートに入れる」だが、決定#42（販売制限は既存維持）を文言にも当てる。
 */
const normalButtonLabel = computed<string>(() =>
  PRODUCT_CONSTANTS.SALES_RESTRICTIONS.includes(props.product.code)
    ? '注文する'
    : (normalBox.value?.buttonLabel ?? '')
);

/** 販売制限の2商品（`KG02` `KF15`）はカートに入れず電話・フォームへ誘導する（決定#42） */
const showRestrictionsDialog = (): void => {
  show(
    `こちらの商品は現在、お電話又はお問合せフォームにて承っております。<br />大変恐れ入りますが、以下よりご連絡をお願い致します。<br />【ご注文先】<br />電話番号：0120-079-996（受付時間 10:00～18:30)<br />お問合せフォーム：<a href="${config.public.frontDomain}/contact" style="color: blue;">${config.public.frontDomain}/contact</a>`,
    ''
  );
};

const submitNormal = async (): Promise<void> => {
  if (PRODUCT_CONSTANTS.SALES_RESTRICTIONS.includes(props.product.code)) {
    showRestrictionsDialog();

    return;
  }

  // ケンコスは販売制限の告知を出し、閉じたらそのままカートへ進む（`KencosNoticeConst`）
  if (
    KENCOS_NOTICE_CONSTANTS.ENABLED &&
    KENCOS_NOTICE_CONSTANTS.PRODUCT_CODES.includes(props.product.code)
  ) {
    await show(KENCOS_NOTICE_CONSTANTS.MESSAGE, KENCOS_NOTICE_CONSTANTS.TITLE);
  }

  const params: CartType.PostCartParams = {
    code: isKencos
      ? PRODUCT_CONSTANTS.KENCOS.COMBINATION_MAP[`${selectedColor.value}${selectedFlavor.value}`]
      : props.product.code,
    quantity: normalQuantity.value,
  };

  await productPresenter.postCart(params);
  await navigateTo('/cart');
};
</script>
